import { forwardRef, ForwardedRef } from 'react'
import { IconArrowLeft } from '@tabler/icons-react'
import { parse } from 'query-string'

import { RecurlyPayment } from 'api'
import Text from 'components/ui/text'
import { Alert } from 'components/ui/alert'
import {
  NewCheckoutModalForm,
  RecurlyContainer,
  PayPal,
  GooglePay,
  RecurlyContext,
  ApplePay,
} from 'features/checkout'
import { translate } from 'utils/translation'
import { cn } from 'utils/classMerge'
import { MIXPANEL_PROPS } from 'utils/mixpanel'
import { useGrowthBookFeature } from 'context/growthbook-context'
import { growthbookFeatures } from 'utils/growthbook'
import { getApiErrorText } from 'features/checkout/utils'
import { useContextSave } from 'hooks/use-context-save'

import { TIER_TITLES } from '../consts'

import { SubscribingPolicy } from './subscribing-policy'

type PaymentListProps = {
  showMobilePaymentForm: boolean
  setShowMobilePaymentForm: (show: boolean) => void
  source: string
}

const PaymentList = ({
  showMobilePaymentForm,
  setShowMobilePaymentForm,
  source,
}: PaymentListProps) => {
  const payPalDisabledFeature = useGrowthBookFeature(
    growthbookFeatures.PAYPAL_DISABLED_REGIONAL,
  )
  const context = useContextSave(RecurlyContext)
  const { isLoading, serverError } = context

  const { aiwAddon: rawAiwAddon = '' } = parse(window.location.search)

  const aiwAddon = Array.isArray(rawAiwAddon) ? rawAiwAddon[0] : rawAiwAddon

  const getSource = () => {
    if (aiwAddon) {
      return MIXPANEL_PROPS.SOURCE.values.AIW_PRICING_PAGE
    }

    return source
  }
  const outerWrapperClassName = payPalDisabledFeature?.isEnabled
    ? 'tw-flex-col-reverse xl:tw-flex-row'
    : 'tw-flex-col md:tw-flex-row md:tw-flex-wrap xl:tw-flex-nowrap'
  const innerWrapperClassName = payPalDisabledFeature?.isEnabled
    ? 'xs: tw-flex-col-reverse'
    : 'tw-flex-col'

  const serverErrorMessage = serverError ? getApiErrorText(serverError) : ''

  return (
    <>
      <div className="tw-flex tw-items-center tw-gap-3">
        {showMobilePaymentForm && (
          <IconArrowLeft
            width={28}
            height={28}
            className="tw-flex tw-cursor-pointer tw-rounded-md tw-border tw-border-solid tw-border-zinc-300 tw-p-1"
            onClick={() => setShowMobilePaymentForm(false)}
          />
        )}
        <Text className="tw-m-0 tw-p-0 tw-text-2xl tw-font-medium tw-text-zinc-950">
          {translate('payment_method')}
        </Text>
      </div>

      <Alert
        open={
          !!serverErrorMessage &&
          serverErrorMessage !==
            translate('old_lifetime_update_billing_info_modal')
        }
        message={serverErrorMessage}
        variant="error"
      />

      <div
        className={cn(
          'tw-relative tw-mt-4 tw-flex tw-gap-2 xs:tw-mt-6 md:tw-justify-between',
          outerWrapperClassName,
        )}
      >
        {isLoading && (
          <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-top-0 tw-z-[99999]" />
        )}
        <div
          className={cn(
            'tw-flex tw-w-full tw-gap-2 xs:tw-flex-row',
            innerWrapperClassName,
          )}
        >
          <PayPal
            className="tw-w-full"
            source={getSource()}
            checkoutSuccessSource={source}
          />
          <ApplePay
            className="tw-w-full"
            source={getSource()}
            checkoutSuccessSource={source}
          />
        </div>
        <GooglePay
          className={cn({
            'tw-pointer-events-none tw-opacity-50': isLoading,
          })}
          source={getSource()}
          checkoutSuccessSource={source}
        />
      </div>
    </>
  )
}

interface CheckoutFormRef {
  refresh: () => void
  continueWithoutFreeTrial: (params: { token: RecurlyPayment }) => void
}

export interface NewRecurlyFormProps {
  tier: string
  interval: string
  featureValue?: boolean
  onContinue: () => void
  onBlockPayment?: () => void
  showMobilePaymentForm: boolean
  setShowMobilePaymentForm: (show: boolean) => void
  isMobile: boolean
  source: string
}

export const NewRecurlyForm = forwardRef<CheckoutFormRef, NewRecurlyFormProps>(
  (props: NewRecurlyFormProps, ref: ForwardedRef<CheckoutFormRef>) => {
    const {
      tier,
      interval,
      featureValue,
      onContinue,
      onBlockPayment,
      showMobilePaymentForm,
      setShowMobilePaymentForm,
      isMobile,
      source,
    } = props

    const planText = translate(TIER_TITLES?.[tier as keyof typeof TIER_TITLES])

    return (
      <RecurlyContainer
        tier={tier}
        interval={interval}
        promoFeature={featureValue}
        onContinue={onContinue}
        onBlockPayment={onBlockPayment}
      >
        <PaymentList
          showMobilePaymentForm={showMobilePaymentForm}
          setShowMobilePaymentForm={setShowMobilePaymentForm}
          source={source}
        />
        <div className="tw-relative tw-flex tw-w-full tw-justify-center before:tw-absolute before:tw-left-0 before:tw-top-1/2 before:tw-h-px before:tw-w-full before:tw-translate-y-1/2 before:tw-bg-zinc-200 before:tw-content-['']">
          <Text className="tw-z-1 tw-tacking-[0.5px] tw-relative tw-my-4 tw-bg-white tw-px-2 tw-py-0 tw-text-center tw-text-xs tw-uppercase tw-text-zinc-500 xs:tw-my-6">
            {translate('pay_with_card')}
          </Text>
        </div>
        <NewCheckoutModalForm
          ref={ref}
          className="tw-mt-0 tw-flex tw-flex-grow tw-justify-start"
          checkoutSuccessSource={
            MIXPANEL_PROPS.SOURCE.values.NEW_CHECKOUT_MODAL
          }
        />
        {isMobile && (
          <SubscribingPolicy className="tw-mt-4" planText={planText} />
        )}
      </RecurlyContainer>
    )
  },
)
